import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Palette } from "lucide-react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { categoryOf, CATEGORIES, initialsOf, isCancelled, minutesOf, prettyType } from "./apptTypes";

// The schedule list has no end time; a block is drawn this many minutes long
// (the providers' slot length on the open-slots endpoint).
export const SLOT_MINUTES = 20;
// startOf("isoWeek") below must not depend on schedule.jsx being imported first
dayjs.extend(isoWeek);
const SNAP = 15;

const hourLabel = (h) => dayjs().hour(h).minute(0).format("h A");
// minutes after midnight -> "h:mm A" by arithmetic (startOf("day").add() is an
// hour off on a DST change day, e.g. 2026-11-01)
const timeLabel = (min) => {
  const h = Math.floor(min / 60) % 24;
  return `${h % 12 || 12}:${String(min % 60).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
const nameOf = (a) => `${a.firstname || ""} ${a.lastname || ""}`.trim() || a.patientName || "Patient";
// In the building right now: a small green dot on the block.
const isActive = (a) => /processing|ready|in-office - checked|checked in|with provider/i.test(String(a.visit_confirmation || ""));

// Side-by-side lanes for appointments that overlap in one column. A run of
// overlaps wider than maxLanes is drawn as 20-minute rows instead: the first
// appointment of each row, then a "+N" block for the rest.
function layoutLanes(items, maxLanes = Infinity) {
  const sorted = [...items].sort((a, b) => a.s - b.s || a.e - b.e);
  const out = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = [];
    cluster.forEach((it) => {
      let i = lanes.findIndex((end) => end <= it.s);
      if (i === -1) {
        i = lanes.length;
        lanes.push(it.e);
      } else lanes[i] = it.e;
      it.lane = i;
    });
    if (lanes.length <= maxLanes) {
      cluster.forEach((it) => out.push({ ...it, lanes: lanes.length }));
    } else {
      const rows = {};
      cluster.forEach((it) => {
        const k = Math.floor(it.s / SLOT_MINUTES) * SLOT_MINUTES;
        (rows[k] = rows[k] || []).push(it);
      });
      Object.keys(rows).forEach((k) => {
        const row = rows[k].sort((x, y) => x.s - y.s);
        const s = Number(k);
        const e = s + SLOT_MINUTES;
        if (row.length === 1) out.push({ ...row[0], s, e, lane: 0, lanes: 1 });
        else {
          out.push({ ...row[0], s, e, lane: 0, lanes: 2 });
          out.push({ more: row.slice(1), s, e, lane: 1, lanes: 2 });
        }
      });
    }
    cluster = [];
  };
  sorted.forEach((it) => {
    if (cluster.length && it.s >= clusterEnd) {
      flush();
      clusterEnd = -1;
    }
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.e);
  });
  if (cluster.length) flush();
  return out;
}

// Hours shown: 8 AM-7 PM, widened to fit any earlier / later appointment.
export function hourRange(appts) {
  let lo = 8;
  let hi = 19;
  appts.forEach((a) => {
    const m = minutesOf(a.starttime);
    lo = Math.min(lo, Math.floor(m / 60));
    hi = Math.max(hi, Math.ceil((m + SLOT_MINUTES) / 60));
  });
  return [Math.max(0, lo), Math.min(24, hi)];
}

const Block = ({ it, pxPerMin, startMin, onOpen, detailed, subOf, dim }) => {
  const a = it.appt;
  const cat = categoryOf(a.type);
  const off = isCancelled(a);
  const top = (it.s - startMin) * pxPerMin + 1;
  const height = Math.max((it.e - it.s) * pxPerMin - 3, 20);
  const showSub = detailed && height >= 34;
  const time = timeLabel(it.s);
  return (
    <button
      type="button"
      className={`bmsc-appt${off ? " off" : ""}${dim ? " dim" : ""}`}
      style={{
        top,
        height,
        left: `calc(${(it.lane / it.lanes) * 100}% + 4px)`,
        width: `calc(${100 / it.lanes}% - 8px)`,
        "--c": cat.color,
        "--t": cat.tint,
      }}
      title={`${nameOf(a)} · ${time} · ${prettyType(a.type) || cat.label}${a.visit_confirmation ? ` · ${a.visit_confirmation}` : ""}`}
      onClick={(e) => onOpen(a, e.currentTarget.getBoundingClientRect())}
    >
      <span className="bmsc-appt-name">{nameOf(a)}</span>
      {showSub && (
        <span className="bmsc-appt-sub">
          {time} · {subOf ? subOf(a) : prettyType(a.type) || cat.label}
        </span>
      )}
      {isActive(a) && !off && <span className="bmsc-appt-live" aria-hidden="true" />}
    </button>
  );
};

/* Time grid shared by the day (one column per provider) and week (one column
   per day) views. Empty space shows a "+ time" ghost and books there. */
const TimeGrid = ({
  onPickHour, columns, startH, endH, hourPx, nowCols, onOpen, detailed, colMin, maxLanes, subOf, dimOf }) => {
  const [ghost, setGhost] = useState(null);
  const pxPerMin = hourPx / 60;
  const startMin = startH * 60;
  const endMin = endH * 60;
  const hours = [];
  for (let h = startH; h <= endH; h++) hours.push(h);
  const now = dayjs();
  const nowMin = now.hour() * 60 + now.minute();
  const showNow = nowCols && nowMin >= startMin && nowMin <= endMin;
  const nowTop = (nowMin - startMin) * pxPerMin;

  const minuteAt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const m = startMin + (e.clientY - r.top) / pxPerMin;
    return Math.max(startMin, Math.min(endMin - SNAP, Math.floor(m / SNAP) * SNAP));
  };

  return (
    <div className="bmsc-grid" style={{ "--cols": columns.length, "--colmin": `${colMin}px` }}>
      <div className="bmsc-corner" />
      {columns.map((c) => (
        <div key={c.key} className={`bmsc-colhead${c.today ? " today" : ""}`}>
          {c.header}
        </div>
      ))}

      <div className="bmsc-gutter" style={{ height: (endH - startH) * hourPx }}>
        {hours.map((h) =>
          onPickHour ? (
            // Every column's appointments in this hour, read in one list.
            <button
              key={h}
              type="button"
              className="bmsc-hour bmsc-hour-btn"
              style={{ top: (h - startH) * hourPx }}
              onClick={() => onPickHour(h)}
              title={`Everything booked at ${hourLabel(h)}`}
            >
              {hourLabel(h)}
            </button>
          ) : (
            <span key={h} className="bmsc-hour" style={{ top: (h - startH) * hourPx }}>
              {hourLabel(h)}
            </span>
          ),
        )}
        {showNow && (
          <span className="bmsc-now-badge" style={{ top: nowTop }}>
            {now.format("h:mm A")}
          </span>
        )}
      </div>

      {columns.map((c) => {
        const items = layoutLanes(
          c.items.map((appt) => {
            const s = minutesOf(appt.starttime);
            return { appt, s, e: s + SLOT_MINUTES };
          }),
          maxLanes,
        );
        const lineHere = showNow && (nowCols === "all" || nowCols.includes(c.key));
        return (
          <div
            key={c.key}
            className={`bmsc-col${c.closed ? " closed" : ""}${c.today ? " today" : ""}`}
            style={{ height: (endH - startH) * hourPx, backgroundSize: `100% ${hourPx}px` }}
            onMouseMove={(e) => {
              if (e.target !== e.currentTarget || !c.onBook) return setGhost(null);
              const m = minuteAt(e);
              if (!ghost || ghost.key !== c.key || ghost.m !== m) setGhost({ key: c.key, m });
            }}
            onMouseLeave={() => setGhost(null)}
            onClick={(e) => {
              if (e.target === e.currentTarget && c.onBook) c.onBook(minuteAt(e));
            }}
          >
            {items.map((it) =>
              it.more ? (
                <button
                  key={`more-${it.s}`}
                  type="button"
                  className="bmsc-more"
                  style={{
                    top: (it.s - startMin) * pxPerMin + 1,
                    height: Math.max((it.e - it.s) * pxPerMin - 3, 20),
                    left: "calc(50% + 2px)",
                    width: "calc(50% - 6px)",
                  }}
                  title={it.more.map((a) => `${nameOf(a)} · ${a.providername}`).join("\n")}
                  onClick={c.onMore}
                >
                  +{it.more.length} more
                </button>
              ) : (
                <Block
                  dim={dimOf ? dimOf(it.appt) : false}
                  key={`${it.appt.appointmentid}-${it.appt.patientid}-${it.s}`}
                  it={it}
                  pxPerMin={pxPerMin}
                  startMin={startMin}
                  onOpen={onOpen}
                  detailed={detailed}
                  subOf={subOf}
                />
              ),
            )}
            {ghost && ghost.key === c.key && (
              <span
                className="bmsc-ghost"
                style={{ top: (ghost.m - startMin) * pxPerMin + 1, height: Math.max(SLOT_MINUTES * pxPerMin - 3, 20) }}
                aria-hidden="true"
              >
                + {timeLabel(ghost.m)}
              </span>
            )}
            {lineHere && <span className="bmsc-now" style={{ top: nowTop }} aria-hidden="true" />}
          </div>
        );
      })}
    </div>
  );
};

/* The columns run wider than the page, so the grid scrolls sideways. Nothing
   said so: the edge just cut off mid-column. This wraps the scroller with a
   fade and a chevron at whichever edge still has something behind it, and -
   when a search is running - brings the first hit into view, since the patient
   you searched for is often under a provider you would have had to scroll to.
   The 76px time gutter is sticky, so a hit is only "visible" past it. */
const GUTTER = 76;

export const ScrollArea = ({ children, focusKey = "", className = "" }) => {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ l: false, r: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ l: el.scrollLeft > 4, r: max > 4 && el.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, children]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !String(focusKey).trim()) return;
    // The first hit in document order: columns are laid out left to right, so
    // this is the leftmost matching provider's earliest appointment.
    const hit = el.querySelector(".bmsc-appt:not(.dim)");
    if (!hit) return;
    const box = el.getBoundingClientRect();
    const h = hit.getBoundingClientRect();
    const hiddenLeft = box.left + GUTTER - h.left;
    const hiddenRight = h.right - box.right;
    let left = el.scrollLeft;
    if (hiddenLeft > 0) left -= hiddenLeft + 16;
    else if (hiddenRight > 0) left += hiddenRight + 16;
    let top = el.scrollTop;
    if (h.top < box.top) top -= box.top - h.top + 12;
    else if (h.bottom > box.bottom) top += h.bottom - box.bottom + 12;
    if (left !== el.scrollLeft || top !== el.scrollTop) {
      el.scrollTo({ left: Math.max(0, left), top: Math.max(0, top), behavior: "smooth" });
    }
    // focusKey only: children is new on every parent render (the 60 s "now"
    // tick), which used to drag the grid back to the hit each minute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  const page = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <div className={`bmsc-hs${edges.l ? " can-l" : ""}${edges.r ? " can-r" : ""} ${className}`}>
      <div className="bmsc-scroll" ref={ref}>
        {children}
      </div>
      <button
        type="button"
        className="bmsc-hs-btn l"
        onClick={() => page(-1)}
        tabIndex={edges.l ? 0 : -1}
        aria-hidden={!edges.l}
        aria-label="Scroll left"
        title="Scroll left"
      >
        <ChevronLeft aria-hidden="true" />
      </button>
      <button
        type="button"
        className="bmsc-hs-btn r"
        onClick={() => page(1)}
        tabIndex={edges.r ? 0 : -1}
        aria-hidden={!edges.r}
        aria-label="Scroll right for more"
        title="Scroll right for more"
      >
        <ChevronRight aria-hidden="true" />
      </button>
    </div>
  );
};

const countOf = (list) => list.filter((a) => !isCancelled(a)).length;
const apptsLabel = (n) => `${n} appointment${n === 1 ? "" : "s"}`;

export const DayView = ({ providers, appts, onOpen, onBook, isToday, dimOf, searchKey, onPickHour }) => {
  const [startH, endH] = hourRange(appts);
  const columns = providers.map((p) => {
    const items = appts.filter((a) => a.providername === p.providername);
    return {
      key: String(p.providerid ?? p.providername),
      items,
      onBook: onBook ? (m) => onBook(p, m) : null,
      header: (
        <>
          <span className="bmsc-avatar">{initialsOf(p.providername)}</span>
          <span className="bmsc-colhead-text">
            <b title={p.providername}>{p.providername}</b>
            <small>{apptsLabel(countOf(items))}</small>
          </span>
        </>
      ),
    };
  });
  if (!columns.length) return <div className="bmsc-empty">No providers to show.</div>;
  return (
    <ScrollArea focusKey={searchKey}>
    <TimeGrid
      onPickHour={onPickHour}
      dimOf={dimOf}
      columns={columns}
      startH={startH}
      endH={endH}
      hourPx={76}
      nowCols={isToday ? "all" : null}
      onOpen={onOpen}
      detailed={false}
      colMin={190}
    />
    </ScrollArea>
  );
};

/* Week: Monday-Sunday (or Monday-Friday, weekends off) columns for the providers ticked in the Providers
   filter. With more than one provider the second line names the provider,
   and crowded stretches collapse to "+N more" (opens that day). */
export const WeekView = ({ weekStart, providers, appts, onOpen, onBook, onPickDay, weekends = true, dimOf, searchKey, onPickHour }) => {
  const names = providers.map((p) => p.providername);
  const mine = appts.filter((a) => names.includes(a.providername));
  const single = providers.length === 1 ? providers[0] : null;
  const [startH, endH] = hourRange(mine);
  const days = Array.from({ length: weekends ? 7 : 5 }, (_, i) => weekStart.add(i, "day"));
  const todayKey = dayjs().format("YYYY-MM-DD");
  const columns = days.map((d) => {
    const key = d.format("YYYY-MM-DD");
    const items = mine.filter((a) => dayjs(a.startdate).format("YYYY-MM-DD") === key);
    const n = countOf(items);
    return {
      key,
      items,
      today: key === todayKey,
      // booking from the week needs one provider; past days are not bookable
      onBook: single && !d.isBefore(dayjs(), "day") ? (m) => onBook(single, m, d) : null,
      onMore: () => onPickDay(d),
      header: (
        <>
          <span className={`bmsc-daynum${key === todayKey ? " on" : ""}`}>{d.date()}</span>
          <span className="bmsc-colhead-text">
            <b>{d.format("dddd")}</b>
            <small>{n ? apptsLabel(n) : "No appointments"}</small>
          </span>
        </>
      ),
    };
  });
  return (
    <ScrollArea focusKey={searchKey}>
      <TimeGrid
        onPickHour={onPickHour}
        dimOf={dimOf}
        columns={columns}
        startH={startH}
        endH={endH}
        hourPx={120}
        nowCols={[todayKey]}
        onOpen={onOpen}
        detailed
        colMin={160}
        maxLanes={2}
        subOf={single ? null : (a) => a.providername}
      />
    </ScrollArea>
  );
};

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const MonthView = ({ month, appts, onPickDay, weekends = true, dimOf }) => {
  const start = month.startOf("month").startOf("isoWeek");
  const end = month.endOf("month").endOf("isoWeek");
  const days = [];
  for (let d = start; !d.isAfter(end, "day"); d = d.add(1, "day")) {
    if (weekends || d.isoWeekday() <= 5) days.push(d);
  }
  const cols = weekends ? 7 : 5;
  const byDay = useMemo(() => {
    const map = {};
    appts.forEach((a) => {
      if (isCancelled(a)) return;
      const k = dayjs(a.startdate).format("YYYY-MM-DD");
      (map[k] = map[k] || []).push(a);
    });
    return map;
  }, [appts]);
  const todayKey = dayjs().format("YYYY-MM-DD");
  return (
    <div className={`bmsc-month cols-${cols}`}>
      {WEEKDAYS.slice(0, cols).map((d) => (
        <div key={d} className="bmsc-month-wd">{d}</div>
      ))}
      {days.map((d) => {
        const key = d.format("YYYY-MM-DD");
        const list = byDay[key] || [];
        const am = list.filter((a) => minutesOf(a.starttime) < 12 * 60).length;
        const pm = list.length - am;
        // Share of the day, printed inside each bar segment. A segment under
        // ~15% is too narrow to hold its label, so it goes without one.
        const amPct = list.length ? Math.round((am / list.length) * 100) : 0;
        const pmPct = list.length ? 100 - amPct : 0;
        // A day greys out only when a search is running and nothing in it matches.
        const dimDay = dimOf ? list.length > 0 && list.every((a) => dimOf(a)) : false;
        return (
          <button
            key={key}
            type="button"
            className={`bmsc-month-day${d.month() !== month.month() ? " out" : ""}${dimDay ? " dim" : ""}`}
            onClick={() => onPickDay(d)}
            title={list.length ? `Morning (AM): ${am}\nAfternoon (PM): ${pm}` : undefined}
          >
            <span className={`bmsc-month-num${key === todayKey ? " on" : ""}`}>{d.date()}</span>
            {list.length ? (
              <>
                <span className="bmsc-month-count">
                  <b>{list.length}</b> appointment{list.length === 1 ? "" : "s"}
                </span>
                <span className="bmsc-ampm">
                  <span className="am"><i />AM <b>{am}</b></span>
                  <span className="pm"><i />PM <b>{pm}</b></span>
                </span>
                <span className="bmsc-mix" aria-hidden="true">
                  {am > 0 && (
                    <span className="am" style={{ flexGrow: am }}>
                      {amPct >= 15 ? `${amPct}%` : ""}
                    </span>
                  )}
                  {pm > 0 && (
                    <span className="pm" style={{ flexGrow: pm }}>
                      {pmPct >= 15 ? `${pmPct}%` : ""}
                    </span>
                  )}
                </span>
              </>
            ) : (
              <span className="bmsc-month-none">No appointments</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/* Colour key, behind a small button in the toolbar. */
export const LegendMenu = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="bmsc-mf" ref={ref}>
      <button
        type="button"
        className="bmsc-legend-btn"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Colour key"
      >
        <Palette aria-hidden="true" />
        <span className="bmsc-legend-txt">Legend</span>
      </button>
      {open && (
        <div className="bmsc-pop bmsc-legend" role="dialog" aria-label="Colour key">
          <div className="bmsc-legend-title">Visit types</div>
          {CATEGORIES.map((c) => (
            <span key={c.key}>
              <i style={{ background: c.color }} />
              {c.label}
            </span>
          ))}
          <div className="bmsc-legend-title">On the schedule</div>
          <span>
            <i className="striped" />
            Cancelled / no-show
          </span>
          <span>
            <i className="live" />
            Patient in the office
          </span>
          <span>
            <i className="now" />
            Current time
          </span>
        </div>
      )}
    </div>
  );
};
